import React, { FC, PropsWithChildren, ReactNode } from 'react';
import { styled } from 'linaria/react';
import { withTheme } from '@shared/lib/theme';
import { badgeBorderRadiusesPx, badgeBorderRadiusesRem, badgeFontSizesPx, badgeFontSizesRem, badgeGapsPx, badgeGapsRem, badgeIconSizesPx, badgeIconSizesRem, badgeLineHeightsPx, badgeLineHeightsRem, badgePaddingsXWithIconPx, badgePaddingsXWithIconRem, badgePaddingsXWithoutIconPx, badgePaddingsXWithoutIconRem, badgePaddingsYPx, badgePaddingsYRem, badgeSizes, badgeTextColor } from '@shared/config/components/badge';
type SizeValue = keyof typeof badgeSizes;
type ResponsiveSize = SizeValue | {
  mobile: SizeValue;
  desktop: SizeValue;
};
interface BadgeProps {
  color?: keyof typeof defaultTheme.colors.badge | string;
  size?: ResponsiveSize;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
}
const Badge: FC<PropsWithChildren<BadgeProps>> = ({
  color = 'popular',
  size = 'l',
  iconStart,
  iconEnd,
  children
}) => {
  const isOnlyIcon = !children;
  const desktopSize = typeof size === 'object' ? size.desktop || 'l' : size || 'l';
  const mobileSize = typeof size === 'object' ? size.mobile || desktopSize : size || 'l';
  return <BadgeWrapper color={color} desktopSize={desktopSize} mobileSize={mobileSize} isWithStartIcon={!!iconStart} isWithEndIcon={!!iconEnd} isOnlyIcon={isOnlyIcon}>
      {iconStart && <IconWrapper color={color} desktopSize={desktopSize} mobileSize={mobileSize}>
          {iconStart}
        </IconWrapper>}
      {children}
      {iconEnd && <IconWrapper color={color} desktopSize={desktopSize} mobileSize={mobileSize}>
          {iconEnd}
        </IconWrapper>}
    </BadgeWrapper>;
};
export default Badge;
interface BadgeWrapperProps extends Required<Pick<BadgeProps, 'color'>> {
  desktopSize: SizeValue;
  mobileSize: SizeValue;
  isWithStartIcon: boolean;
  isWithEndIcon: boolean;
  isOnlyIcon: boolean;
}
const _exp = /*#__PURE__*/() => ({
  desktopSize
}) => badgeGapsRem[desktopSize];
const _exp2 = /*#__PURE__*/() => ({
  color,
  theme
}) => color in theme.colors.badge ? theme.colors.badge[color] : color;
const _exp4 = /*#__PURE__*/() => ({
  desktopSize
}) => badgePaddingsYRem[desktopSize];
const _exp5 = /*#__PURE__*/() => ({
  desktopSize,
  isWithStartIcon,
  isOnlyIcon
}) => isWithStartIcon || isOnlyIcon ? badgePaddingsXWithIconRem[desktopSize] : badgePaddingsXWithoutIconRem[desktopSize];
const _exp6 = /*#__PURE__*/() => ({
  desktopSize,
  isWithEndIcon,
  isOnlyIcon
}) => isWithEndIcon || isOnlyIcon ? badgePaddingsXWithIconRem[desktopSize] : badgePaddingsXWithoutIconRem[desktopSize];
const _exp7 = /*#__PURE__*/() => ({
  desktopSize
}) => badgeBorderRadiusesRem[desktopSize];
const _exp8 = /*#__PURE__*/() => ({
  color,
  theme
}) => badgeTextColor(color as keyof typeof defaultTheme.colors.badge, theme);
const _exp9 = /*#__PURE__*/() => ({
  theme
}) => theme.typography.family.MTSCompact;
const _exp0 = /*#__PURE__*/() => ({
  theme
}) => theme.typography.weight.medium;
const _exp1 = /*#__PURE__*/() => ({
  desktopSize
}) => badgeFontSizesRem[desktopSize];
const _exp10 = /*#__PURE__*/() => ({
  desktopSize
}) => badgeLineHeightsRem[desktopSize];
const _exp11 = /*#__PURE__*/() => ({
  theme
}) => theme.typography.letterSpacing.rem.pS;
const _exp12 = /*#__PURE__*/() => ({
  mobileSize
}) => badgeGapsPx[mobileSize];
const _exp14 = /*#__PURE__*/() => ({
  mobileSize
}) => badgePaddingsYPx[mobileSize];
const _exp15 = /*#__PURE__*/() => ({
  mobileSize,
  isWithStartIcon,
  isOnlyIcon
}) => isWithStartIcon || isOnlyIcon ? badgePaddingsXWithIconPx[mobileSize] : badgePaddingsXWithoutIconPx[mobileSize];
const _exp16 = /*#__PURE__*/() => ({
  mobileSize,
  isWithEndIcon,
  isOnlyIcon
}) => isWithEndIcon || isOnlyIcon ? badgePaddingsXWithIconPx[mobileSize] : badgePaddingsXWithoutIconPx[mobileSize];
const _exp17 = /*#__PURE__*/() => ({
  mobileSize
}) => badgeBorderRadiusesPx[mobileSize];
const _exp18 = /*#__PURE__*/() => ({
  mobileSize
}) => badgeFontSizesPx[mobileSize];
const _exp19 = /*#__PURE__*/() => ({
  mobileSize
}) => badgeLineHeightsPx[mobileSize];
const _exp20 = /*#__PURE__*/() => ({
  theme
}) => theme.typography.letterSpacing.px.pS;
const BadgeWrapper = withTheme(/*#__PURE__*/styled('div')({
  name: "BadgeWrapper",
  class: "b1e5mzrg",
  propsAsIs: false,
  vars: {
    "b1e5mzrg-0": [_exp()],
    "b1e5mzrg-1": [_exp2()],
    "b1e5mzrg-2": [_exp4()],
    "b1e5mzrg-3": [_exp5()],
    "b1e5mzrg-4": [_exp6()],
    "b1e5mzrg-5": [_exp7()],
    "b1e5mzrg-6": [_exp8()],
    "b1e5mzrg-7": [_exp9()],
    "b1e5mzrg-8": [_exp0()],
    "b1e5mzrg-9": [_exp1()],
    "b1e5mzrg-10": [_exp10()],
    "b1e5mzrg-11": [_exp11()],
    "b1e5mzrg-12": [_exp12()],
    "b1e5mzrg-13": [_exp14()],
    "b1e5mzrg-14": [_exp15()],
    "b1e5mzrg-15": [_exp16()],
    "b1e5mzrg-16": [_exp17()],
    "b1e5mzrg-17": [_exp18()],
    "b1e5mzrg-18": [_exp19()],
    "b1e5mzrg-19": [_exp20()]
  }
}));
interface IconWrapperProps extends Required<Pick<BadgeProps, 'color'>> {
  desktopSize: SizeValue;
  mobileSize: SizeValue;
}
const _exp22 = /*#__PURE__*/() => ({
  desktopSize
}) => badgeIconSizesRem[desktopSize];
const _exp24 = /*#__PURE__*/() => ({
  color,
  theme
}) => badgeTextColor(color as keyof typeof defaultTheme.colors.badge, theme);
const _exp26 = /*#__PURE__*/() => ({
  mobileSize
}) => badgeIconSizesPx[mobileSize];
const IconWrapper = withTheme(/*#__PURE__*/styled('div')({
  name: "IconWrapper",
  class: "i1x1wgu9",
  propsAsIs: false,
  vars: {
    "i1x1wgu9-0": [_exp22()],
    "i1x1wgu9-1": [_exp24()],
    "i1x1wgu9-2": [_exp26()]
  }
}));

require("./Badge.linaria.module.css!=!../../../../../node_modules/linaria/node_modules/@linaria/webpack4-loader/lib/outputCssLoader.js?cacheProvider=!./Badge.tsx");